import type { WidgetEvent } from './event';
import { warnOnce } from './warn';

export type FetchLike = typeof fetch;

/**
 * Collect events and post them in one request.
 *
 * An answer and the completion that follows it happen in the same tick, so batching turns two
 * requests into one. The queue is cleared before the request goes out: a failed post is dropped
 * rather than retried, because a widget that keeps hammering a publisher's page after a network
 * problem is worse than a lost event.
 */
export interface Sender {
  send(...events: WidgetEvent[]): void;
  flush(): Promise<void>;
  readonly pending: number;
}

export function createSender(endpoint: string, fetchImpl: FetchLike): Sender {
  let queue: WidgetEvent[] = [];

  return {
    get pending() {
      return queue.length;
    },
    send(...events: WidgetEvent[]): void {
      for (const event of events) queue.push(event);
    },
    async flush(): Promise<void> {
      if (queue.length === 0) return;
      const batch = queue;
      queue = [];
      try {
        await fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ events: batch }),
          // The reader may be navigating away mid vote. keepalive is what lets the browser finish
          // the request after the page has gone.
          keepalive: true,
          credentials: 'omit',
          mode: 'cors',
        });
      } catch (error) {
        warnOnce('could not deliver events', error);
      }
    },
  };
}

/** A GET that returns parsed JSON, or null for anything that is not a usable response. */
export async function getJson(url: string, fetchImpl: FetchLike): Promise<unknown> {
  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!response.ok) return null;
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}
